import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { companyService } from "../../app/services";

const ManufacturersSection = () => {
  const { t } = useTranslation();

  const companies = [
    { name: "2PointZero", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000YQISNIA5" },
    { name: "6DOF Engineering and Trading", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000VnYwPIAV" },
    { name: "Aaraya Chocolates", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000YeE8jIAF" },
    { name: "ABB Transmission & Distribution", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000QYZq2IAH" },
    { name: "AD Ports Group", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000QYjCZIA1" },
    { name: "ADNOC", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000UahV0IAJ" },
    { name: "ADNOC Distribution", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000QYYt2IAH" },
    { name: "Advanced Armour Engineering", url: "https://map.miite.ae/places/by-vendor-id/001Hp00003Vrly9IAB" },
    { name: "Advanced Casting Technologies", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000WvMcTIAV" },
    { name: "Advanced Steel Industries", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000SqJ4rIAF" },
    { name: "National Paints", url: "https://map.miite.ae/" },
    { name: "Baker Hughes", url: "https://map.miite.ae/" },
    { name: "EGA", url: "https://map.miite.ae/" },
    { name: "Ghobash", url: "https://map.miite.ae/" },
    { name: "YOKOGAWA", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000T5oMrIAJ" },
    { name: "ADQCC", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000QYdOEIA1" },
    { name: "ADOS", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000PfasNIAR" },
    { name: "ADPAC", url: "https://map.miite.ae/places/by-vendor-id/001Ql00000V2wVvIAJ" },
  ];

  // Normalizer shared with DB lookups
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$|/g, "");
  const initials = (name) => name.split(/\s+/).map(w => w[0]).join('').slice(0,3).toUpperCase();

  // Build logo map from Companies DB, keyed by normalized company name
  const [logoMap, setLogoMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    async function loadLogosFromDb() {
      try {
        // Fetch categories first
        const categories = await companyService.getAllCategories().catch(() => []);
        const catValues = Array.isArray(categories)
          ? categories.map((c) => c.name || c.Name || c.id || c.Id).filter(Boolean)
          : [];

        if (!catValues.length) return;

        // Limit how many categories we scan to keep it light on Home
        const toScan = catValues.slice(0, 10);
        const requests = toScan.map((cv) =>
          companyService
            .getCompaniesByCategoryAndLocationAndCompanyidNotConnected(cv, {}, "")
            .catch(() => [])
        );
        const results = await Promise.all(requests);

        if (cancelled) return;

        const map = {};
        results.flat().forEach((c) => {
          const nm = (c?.name || c?.Name || c?.username || c?.Username || "").trim();
          if (!nm) return;
          const key = norm(nm);
          const logo = c?.logo || c?.Logo || null;
          if (logo && !map[key]) map[key] = logo;
        });

        setLogoMap(map);
      } catch (e) {
        // Silent fail: section still renders with fallbacks
      }
    }
    loadLogosFromDb();
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="members" style={{ padding: "70px 10%", backgroundColor: "#fff" }}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <h2 style={{ fontSize: 32, fontWeight: 800, color: "#111", margin: 0 }}>
          {t("EmiratesMade.Manufacturers.topTitle", { defaultValue: "Top 2025 Leading Companies" })}
        </h2>
      </div>

      <style>{`
        .logos-wrap{max-width:1100px;margin:0 auto;background:#fff;border-radius:16px;padding:26px 22px;box-shadow:0 12px 28px rgba(0,0,0,.08)}
        .logos-grid{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:18px;align-items:center}
        .logo-card{height:72px;background:#fff;border:1px solid rgba(90,62,200,.15);border-radius:12px;display:flex;align-items:center;justify-content:center;overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,.05);transition:transform .2s ease,box-shadow .2s ease}
        .logo-card:hover{transform:translateY(-2px);box-shadow:0 8px 18px rgba(0,0,0,.08)}
        .logo-card img{max-height:60px;max-width:90%;object-fit:contain;display:block;filter:grayscale(10%)}
        .logo-fallback{height:60px;width:90%;display:flex;align-items:center;justify-content:center;color:#222;font-weight:800;background:linear-gradient(135deg,#f6f7fb,#eef0f7);border-radius:8px}
        @media(max-width:1100px){.logos-grid{grid-template-columns:repeat(5,1fr)}}
        @media(max-width:900px){.logos-grid{grid-template-columns:repeat(4,1fr)}}
        @media(max-width:680px){.logos-grid{grid-template-columns:repeat(3,1fr)}}
        @media(max-width:520px){.logos-grid{grid-template-columns:repeat(2,1fr)}}
      `}</style>

      <div className="logos-wrap" aria-label="Leading companies logos">
        <div className="logos-grid">
          {companies.map((c, i) => {
            const key = norm(c.name);
            const src = (typeof logoMap === 'object' && logoMap[key]) || null;
            return (
              <a key={i} href={c.url} target="_blank" rel="noreferrer" className="logo-card" aria-label={c.name}>
                {src ? (
                  <img
                    src={src}
                    alt={c.name}
                    loading="lazy"
                    onError={(e)=>{
                      e.currentTarget.onerror=null;
                      const div=document.createElement('div');
                      div.className='logo-fallback';
                      div.innerText=initials(c.name);
                      e.currentTarget.replaceWith(div);
                    }}
                  />
                ) : (
                  <div className="logo-fallback">{initials(c.name)}</div>
                )}
              </a>
            );
          })}
        </div>
      </div>

      {/* <div style={{ textAlign: "center", marginTop: 26 }}>
        <a
          href="https://map.miite.ae/"
          style={{
            display: "inline-block",
            background: "var(--uae-green)",
            color: "#fff",
            textDecoration: "none",
            padding: "12px 22px",
            borderRadius: 12,
            fontWeight: 800,
            boxShadow: "0 10px 20px rgba(90,62,200,.25)",
          }}
        >
          {t("EmiratesMade.Manufacturers.fullList", { defaultValue: "View the full exhibitor list" })}
        </a>
      </div> */}
    </section>
  );
};

export default ManufacturersSection;



